import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import * as Opt from 'fp-ts/Option';
import { flow, pipe } from 'fp-ts/function';
import { useCampaignsDataQuery, useLazyTemplatesPreviewQuery } from 'modules/ai/hooks';
import { CAMPAIGN_TYPE_CONTENT_MAPPER } from 'modules/campaigns/lib/utils';
import {
  TopCampaignData,
  MessageTypeRowData,
  MessageTypeCampaignData,
} from 'modules/intelligentInsights/frequencyOptimization/types';

export const fetchAllCampaigns = gql`
  query getCampaigns($ids: [Long!]) {
    campaigns(ids: $ids) {
      results {
        id
        name
        templateId
        campaignTypeDefinition
      }
    }
  }
`;

type TemplatesData = { [templateId: number]: string };
type CampaignsMessageTypeMap = { [campaignId: number]: Array<number> };
type TrackedMessageTypeCampaignsData = MessageTypeCampaignData & {
  messageTypeIndexes: Array<number>;
};

export const getCampaignsData = (fetchedQampaignsData: Array<TopCampaignData>) =>
  Opt.fromNullable(isEmpty(fetchedQampaignsData) ? null : fetchedQampaignsData);

export const getCampaignIdData = (messageTypeRows: Array<MessageTypeRowData>) => {
  const messageTypeIdsMap: CampaignsMessageTypeMap = {};
  const allCampaignIds = messageTypeRows.flatMap((messageType) => {
    return (messageType.topCampaignIds || []).map((campaignId) => {
      messageTypeIdsMap[campaignId] = (messageTypeIdsMap[campaignId] || []).concat(
        messageType.messageTypeId,
      );
      return campaignId;
    });
  });

  return {
    messageTypeIdsMap,
    allCampaignIds: allCampaignIds.filter((value, index, self) => self.indexOf(value) === index),
  };
};

const parseTemplateIds = Opt.map<Array<TopCampaignData>, number[]>((maybeCampaigns) =>
  maybeCampaigns.map((campaign) => campaign.templateId),
);
const getTemplateIds = flow(
  getCampaignsData,
  parseTemplateIds,
  Opt.getOrElse(() => [] as number[]),
);

const getTemplatesData = (templatesData: TemplatesData) =>
  Opt.fromNullable(isEmpty(templatesData) ? null : templatesData);

export const campaignsEnrichmentDataCreator =
  (mtIdsMap: CampaignsMessageTypeMap) =>
  (templatesDataWithDefault: TemplatesData) =>
  (campaigns: Array<TopCampaignData>) => {
    return campaigns.map((campaign): TrackedMessageTypeCampaignsData => {
      const messageTypeIdIndexes = mtIdsMap[campaign.id];
      const { displayName } = CAMPAIGN_TYPE_CONTENT_MAPPER[campaign.campaignTypeDefinition] || {};
      return {
        campaignId: campaign.id,
        campaignName: campaign.name,
        templateUrl: templatesDataWithDefault[campaign.templateId] || '',
        campaignType: displayName || '',
        messageTypeIndexes: messageTypeIdIndexes || [],
      };
    });
  };

const deriveCampaignsForMessageTypes = (
  parsedCampaignsData: Opt.Option<Array<TopCampaignData>>,
  parsedTemplatesData: Opt.Option<TemplatesData>,
  mtIdsMap: CampaignsMessageTypeMap,
) => {
  // Continue processing of campaigns even if templates data is not available
  const createCampaignsEnrichmentData = pipe(
    parsedTemplatesData,
    Opt.getOrElse(() => ({}) as TemplatesData),
    campaignsEnrichmentDataCreator(mtIdsMap),
  );

  return pipe(parsedCampaignsData, Opt.map(createCampaignsEnrichmentData));
};

export const enrichMessageTypeRowWithCampaignData = (
  messageTypeRows: Array<MessageTypeRowData>,
  campaigns: Array<TopCampaignData>,
  templatesData: TemplatesData,
  mtIdsMap: CampaignsMessageTypeMap,
) => {
  const parsedCampaignsData = getCampaignsData(campaigns);
  const parsedTemplatesData = getTemplatesData(templatesData);

  if (Opt.isNone(parsedCampaignsData)) {
    return messageTypeRows;
  }

  const calculatedCampaignsData = deriveCampaignsForMessageTypes(
    parsedCampaignsData,
    parsedTemplatesData,
    mtIdsMap,
  );
  return pipe(
    calculatedCampaignsData,
    Opt.map<
      Array<MessageTypeCampaignData & { messageTypeIndexes: Array<number> }>,
      Array<MessageTypeRowData>
    >((calculatedData) => {
      return messageTypeRows.map((messageType) => {
        const campaignsForMessageType = calculatedData.filter((campaign) =>
          campaign.messageTypeIndexes.includes(messageType.messageTypeId),
        );
        return {
          ...messageType,
          // Make message type match expected type
          campaigns: campaignsForMessageType.map<MessageTypeCampaignData>(
            ({ messageTypeIndexes, ...rest }) => rest,
          ),
        };
      });
    }),
    Opt.getOrElse(() => messageTypeRows),
  );
};

export const useMessageTypeCampaignDataQuery = (
  messageTypeRows: Array<MessageTypeRowData>,
  isFroDataLoading: boolean,
  isFroDataError: boolean,
) => {
  const { messageTypeIdsMap, allCampaignIds } = getCampaignIdData(messageTypeRows);
  const {
    data: campaignsData,
    isLoading: isCampaignsDataLoading,
    isError: isCampaignsDataError,
  } = useCampaignsDataQuery<TopCampaignData>(
    fetchAllCampaigns,
    isFroDataLoading,
    isFroDataError,
    allCampaignIds,
  );

  const {
    mutate,
    isLoading: isTemplatesDataLoading,
    isError: isTemplatesDataError,
    data: templatesData,
  } = useLazyTemplatesPreviewQuery();

  useEffect(() => {
    if (isCampaignsDataLoading || isCampaignsDataError || isEmpty(campaignsData)) {
      return;
    }

    mutate({
      templateIds: getTemplateIds(campaignsData),
    });
  }, [campaignsData, isCampaignsDataLoading, isCampaignsDataError]);

  return {
    isLoading: isCampaignsDataLoading || isTemplatesDataLoading,
    isError: isCampaignsDataError || isTemplatesDataError,
    isReady: !isEmpty(campaignsData) && !isEmpty(templatesData),
    data: enrichMessageTypeRowWithCampaignData(
      messageTypeRows,
      campaignsData,
      templatesData,
      messageTypeIdsMap,
    ),
  };
};
